import * as React from 'react';
import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Grid, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import NumberSelector from '../../../common/ui/NumberSelector';
import { AccountData, OrderData, OrderService } from '../../../api';
import { getTimeFormat } from '../../../common/getDateAndTimeFormat';
import { useAuth } from '../../../session/InternalAuthProvider';
import { isExternalVoucherUnapproved } from '../../../externalVouchers/utils';
import { usePriceFormatter } from '../../../common/usePriceFormatter';
import { ColorButton } from '../../../common/ui/ConfimationDialog';
import { useSafeExecute } from '../../../common/apiNotifications/safeExecute';

type CheckinDialogProps = {
  order?: OrderData;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  updateItem: (itemToUpdate: OrderData) => Promise<void>;
};
export default function CheckinDialog({ order, isOpen, setIsOpen, updateItem }: CheckinDialogProps) {
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const safeExecute = useSafeExecute();
  const [persons, setPersons] = useState(0);
  const { t } = useTranslation();
  const { authState } = useAuth();
  const priceFormatter = usePriceFormatter();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account || undefined);
  const [comment, setComment] = useState(order?.comment);
  // Settled on a third-party code nobody has cleared yet - the booking is not good for entry.
  const isBlockedByExternalVoucher = isExternalVoucherUnapproved(order);
  const onSaveHandler = async () => {
    setLoading(true);
    if (order) {
      const checkedInOrder = await OrderService.checkinOrder({
        order_id: order.id,
        persons,
        comment,
        override: !!order.checkin,
      });

      await updateItem({ ...checkedInOrder });
    }

    setIsOpen(false);
    setLoading(false);
  };

  const onCancelCheckinHandler = async () => {
    if (!order) return;
    setCancelLoading(true);
    try {
      const updatedOrder = await safeExecute({
        apiCall: () => OrderService.cancelCheckinOrder({ order_id: order.id }),
        successMessage: t('checkin_dialog.cancel_checkin_success'),
      });
      await updateItem({ ...updatedOrder });
      setIsOpen(false);
    } finally {
      setCancelLoading(false);
    }
  };

  useMemo(() => {
    if (order) {
      let defaultPersons = order.persons;
      if (order && order.checkin) {
        defaultPersons = order.checkin.persons;
      }
      setPersons(defaultPersons);
      setComment(order.comment);
    }
  }, [order]);

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{t('checkin_dialog.title')}</DialogTitle>
        {order && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {order.persons} {order.persons === 1 ? t('checkin_dialog.person') : t('checkin_dialog.people')} •{' '}
              <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {order.time ? moment.unix(order.time).tz('utc').format(timeFormat) : ''}
              </span>{' '}
              • {order.firstname} {order.lastname}
            </Typography>
          </Box>
        )}
        <DialogContent>
          {isBlockedByExternalVoucher && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('checkin_dialog.external_voucher_not_approved')}
            </Alert>
          )}
          {order && (
            <Grid container flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
              <NumberSelector
                value={persons}
                setValue={setPersons}
                disabled={order.persons === 1 || isBlockedByExternalVoucher}
                min={0}
              />
              {!!order.extra_items?.length && (
                <Grid container flexDirection="column" gap={0.5}>
                  <Typography fontWeight="700">{t('checkin_dialog.chosen_projects')}</Typography>
                  {order.extra_items.map((line) => (
                    <Grid key={line.id} container justifyContent="space-between" alignItems="center" wrap="nowrap">
                      <Typography variant="body2">
                        {line.quantity} × {line.label}
                      </Typography>
                      {priceFormatter(line.quantity * line.unit_price)}
                    </Grid>
                  ))}
                </Grid>
              )}
              <TextField
                multiline
                fullWidth
                disabled={isBlockedByExternalVoucher}
                label={t('checkin_dialog.comments')}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {order?.checkin && (
            <LoadingButton
              loading={cancelLoading}
              disabled={loading}
              color="error"
              onClick={onCancelCheckinHandler}
              sx={{ marginInlineEnd: 'auto' }}>
              {t('checkin_dialog.cancel_checkin')}
            </LoadingButton>
          )}
          <ColorButton onClick={() => setIsOpen(false)}>{t('cancel')}</ColorButton>
          <LoadingButton
            loading={loading}
            disabled={isBlockedByExternalVoucher || cancelLoading}
            variant="contained"
            onClick={onSaveHandler}
            autoFocus>
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
